// missionDetailsPdf.ts
// CREATED: PDF generation for mission details, following the same pattern as teamSchedulePdf.ts
// Produces a portrait A4 PDF with mission metadata, task checklist, and recurrence info.
// UPDATED: Fixed multi-line title truncation, dynamic row heights, smart badge placement,
//          French accent corrections, improved number/checkbox/text alignment, synced page estimator.
// UPDATED: Fixed page estimator to account for 10mm "no tasks" fallback block when items is empty.
// UPDATED: Added handwritten input box for task items that require text input, replacing the old placeholder hint.
// UPDATED: Removed redundant "Saisie requise"/"Saisie libre" badge when input box is present; added "Facultatif"
//          label for optional inputs; tightened spacing; dotted writing guideline; removed zebra stripe;
//          added subtle row separators.

import jsPDF from 'jspdf';
import type { TaskItem, RecurrenceRule } from './supabase';
import { formatRecurrenceText } from './recurrence';

export interface MissionPdfService {
  id: string;
  name: string;
  color: string;
}

export interface GenerateMissionDetailsPdfOptions {
  name: string;
  description?: string | null;
  estimated_duration_minutes?: number | null;
  service?: MissionPdfService | null;
  items: TaskItem[];
  recurrence?: RecurrenceRule | null;
}

type RGB = [number, number, number];

// --- Shared color palette (mirrors teamSchedulePdf.ts) ---
const SERVICE_PALETTES: Record<string, { bg: RGB; border: RGB; text: RGB }> = {
  blue:    { bg: [219, 234, 254], border: [147, 197, 253], text: [30,  64, 175] },
  cyan:    { bg: [207, 250, 254], border: [103, 232, 249], text: [21,  94, 117] },
  teal:    { bg: [204, 251, 241], border: [94,  234, 212], text: [17,  94,  89] },
  lime:    { bg: [236, 252, 203], border: [190, 242, 100], text: [63,  98,  18] },
  orange:  { bg: [255, 237, 213], border: [253, 186, 116], text: [154, 52,  18] },
  amber:   { bg: [254, 243, 199], border: [252, 211,  77], text: [146, 64,  14] },
  rose:    { bg: [255, 228, 230], border: [253, 164, 175], text: [159, 18,  57] },
  pink:    { bg: [252, 231, 243], border: [249, 168, 212], text: [157, 23,  77] },
  violet:  { bg: [237, 233, 254], border: [196, 181, 253], text: [91,  33, 182] },
  fuchsia: { bg: [250, 232, 255], border: [240, 171, 252], text: [134, 25, 143] },
  sky:     { bg: [224, 242, 254], border: [125, 211, 252], text: [7,   89, 133] },
  indigo:  { bg: [224, 231, 255], border: [165, 180, 252], text: [55,  48, 163] },
};

const FALLBACK_PALETTE = {
  bg:     [243, 244, 246] as RGB,
  border: [209, 213, 219] as RGB,
  text:   [31,   41,  55] as RGB,
};

function getServicePalette(colorName?: string) {
  if (!colorName) return FALLBACK_PALETTE;
  return SERVICE_PALETTES[colorName] ?? FALLBACK_PALETTE;
}

// --- Design system colors ---
const C_HEADER_BG:    RGB = [26,  68,  60];
const C_HEADER_TEXT:  RGB = [255, 255, 255];
const C_SECTION_BG:   RGB = [248, 250, 252];
const C_BORDER:       RGB = [226, 232, 240];
const C_TITLE_TEXT:   RGB = [15,  23,  42];
const C_BODY_TEXT:    RGB = [51,  65,  85];
const C_META_TEXT:    RGB = [100, 116, 139];
const C_FOOTER_TEXT:  RGB = [156, 163, 175];
const C_FOOTER_LINE:  RGB = [203, 213, 225];
const C_RECUR_BG:     RGB = [240, 249, 255];
const C_RECUR_BORDER: RGB = [186, 230, 253];
const C_RECUR_TEXT:   RGB = [12,  74, 110];
const C_BADGE_BG:     RGB = [241, 245, 249];
const C_BADGE_TEXT:   RGB = [71,  85, 105];
const C_CHECK_BOX:    RGB = [203, 213, 225];
const C_ROW_SEP:      RGB = [226, 232, 240];

// --- Layout constants (mm) ---
const MARGIN       = 12;
const HEADER_H     = 14;
const META_H       = 5;
const FOOTER_H     = 8;

// --- Task row layout constants ---
const ROW_NUM_W    = 7;    // width reserved for the number column
const ROW_CHECK_W  = 5.5;  // checkbox size + gap before text
const ROW_CHECK_SZ = 4.5;  // actual checkbox square size
const TITLE_FS     = 8.5;  // task title font size (pt)
const TITLE_LS     = 4.8;  // title line-height (mm)
const BADGE_FS     = 6.5;  // badge font size (pt)
const BADGE_PAD_H  = 2.8;  // badge horizontal padding
const BADGE_PAD_V  = 1.6;  // badge vertical padding
const ROW_PAD_TOP  = 3.5;  // padding above first text line inside the row
const ROW_PAD_BOT  = 3.5;  // padding below last element inside the row
const ROW_GAP      = 1;    // gap between consecutive rows (reduced from 1.5)

// --- Text input box constants ---
const INPUT_BOX_H     = 7.5;  // height of the handwritten input area (mm) (reduced from 9)
const INPUT_GAP       = 1.5;  // gap between title area and the input box (reduced from 2.5)
const INPUT_LABEL_H   = 4;    // height reserved for the Obligatoire/Facultatif label (mm)
const INPUT_FS        = 7;    // font size for placeholder text inside the box
const C_INPUT_BG:    RGB = [255, 255, 255];
const C_INPUT_BORDER: RGB = [148, 163, 184];
const C_REQUIRED_TEXT: RGB = [180, 83, 9];

// --- Utility ---

function normalizeSlug(str: string): string {
  return str
    .toLowerCase()
    .replace(/[éèêë]/g, 'e').replace(/[àâä]/g, 'a').replace(/[ùûü]/g, 'u')
    .replace(/[îï]/g, 'i').replace(/[ôö]/g, 'o').replace(/ç/g, 'c')
    .replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').slice(0, 50);
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h${m.toString().padStart(2, '0')}` : `${h}h`;
}

// Returns the height (mm) of a single badge at BADGE_FS
// --- Shared task row height calculator ---
// Called identically in the render pass and the estimation pass so they always agree.
interface TaskRowMetrics {
  titleLines: string[];
  rowH: number;
}

function measureTaskRow(doc: jsPDF, item: TaskItem, textMaxW: number): TaskRowMetrics {
  doc.setFontSize(TITLE_FS);
  doc.setFont('helvetica', 'bold');
  const titleLines = doc.splitTextToSize(item.title, textMaxW) as string[];

  const hasTextInput = !!item.requires_text_input;

  // Compose the height
  const titleBlockH  = titleLines.length * TITLE_LS;
  // Input box: gap + label (always present for required or optional) + the box itself
  const inputBoxExtraH = hasTextInput
    ? INPUT_GAP + INPUT_LABEL_H + INPUT_BOX_H
    : 0;
  const rowH = ROW_PAD_TOP + titleBlockH + inputBoxExtraH + ROW_PAD_BOT;

  return { titleLines, rowH };
}

// --- Logo loader (shared with teamSchedulePdf pattern) ---
async function loadLogo(url: string, timeoutMs = 5000): Promise<{ data: string; aspect: number } | null> {
  return new Promise(resolve => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    const timer = setTimeout(() => {
      img.onload = null;
      img.onerror = null;
      img.src = '';
      resolve(null);
    }, timeoutMs);

    img.onload = () => {
      clearTimeout(timer);
      try {
        const canvas = document.createElement('canvas');
        canvas.width  = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(null); return; }
        ctx.drawImage(img, 0, 0);
        resolve({ data: canvas.toDataURL('image/png'), aspect: img.naturalWidth / img.naturalHeight });
      } catch { resolve(null); }
    };
    img.onerror = () => {
      clearTimeout(timer);
      resolve(null);
    };
    img.src = url;
  });
}

// --- Drawing helpers ---

function drawHeader(
  doc: jsPDF,
  x: number, y: number, w: number,
  missionName: string,
  logo: { data: string; aspect: number } | null,
) {
  doc.setFillColor(...C_HEADER_BG);
  doc.rect(x, y, w, HEADER_H, 'F');

  const logoH = 8;
  let textStartX = x + 5;

  if (logo) {
    const logoW = Math.min(logoH * logo.aspect, 44);
    const logoY = y + (HEADER_H - logoH) / 2;
    doc.addImage(logo.data, 'PNG', x + 4, logoY, logoW, logoH);
    textStartX = x + 4 + logoW + 5;
  }

  doc.setTextColor(...C_HEADER_TEXT);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Fiche Mission', textStartX, y + HEADER_H / 2 + 2.2);

  const truncatedName = missionName.length > 40 ? missionName.slice(0, 37) + '...' : missionName;
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.text(truncatedName, x + w - 3, y + HEADER_H / 2 + 2.2, { align: 'right' });
}

function drawMetaLine(
  doc: jsPDF,
  x: number, y: number, w: number,
  generatedAt: string, pageNum: number, totalPages: number,
) {
  doc.setTextColor(...C_META_TEXT);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  doc.text(`Généré le ${generatedAt}`, x, y + 3.5);
  doc.text(`Page ${pageNum} / ${totalPages}`, x + w, y + 3.5, { align: 'right' });
}

function drawFooter(doc: jsPDF, x: number, pageH: number, w: number, missionName: string) {
  const lineY = pageH - MARGIN - FOOTER_H + 1;
  doc.setDrawColor(...C_FOOTER_LINE);
  doc.setLineWidth(0.25);
  doc.line(x, lineY, x + w, lineY);

  doc.setTextColor(...C_FOOTER_TEXT);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  doc.text('TASQ - Fiche Mission', x, lineY + 4);
  const truncated = missionName.length > 50 ? missionName.slice(0, 47) + '...' : missionName;
  doc.text(truncated, x + w, lineY + 4, { align: 'right' });
}

// Returns the drawn badge width
function drawBadge(
  doc: jsPDF,
  label: string,
  bx: number, by: number,
  bg: RGB, border: RGB, text: RGB,
  fontSize = BADGE_FS,
): number {
  doc.setFontSize(fontSize);
  doc.setFont('helvetica', 'normal');
  const textW  = doc.getTextWidth(label);
  const bw     = textW + BADGE_PAD_H * 2;
  const bh     = fontSize * 0.352778 + BADGE_PAD_V * 2;

  doc.setFillColor(...bg);
  doc.setDrawColor(...border);
  doc.setLineWidth(0.2);
  doc.roundedRect(bx, by, bw, bh, 1.2, 1.2, 'FD');

  doc.setTextColor(...text);
  doc.text(label, bx + BADGE_PAD_H, by + bh / 2 + fontSize * 0.352778 / 2 - 0.3);
  return bw;
}

function drawCheckbox(doc: jsPDF, cx: number, cy: number) {
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(...C_CHECK_BOX);
  doc.setLineWidth(0.5);
  doc.roundedRect(cx, cy, ROW_CHECK_SZ, ROW_CHECK_SZ, 0.8, 0.8, 'FD');
}

function drawTextInputBox(
  doc: jsPDF,
  x: number, y: number, w: number,
  placeholder: string | null | undefined,
  isRequired: boolean,
): void {
  let curY = y + INPUT_GAP;

  // Always show a label: "Obligatoire" (amber) or "Facultatif" (gray)
  doc.setFontSize(6.5);
  if (isRequired) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...C_REQUIRED_TEXT);
    doc.text('Obligatoire', x, curY + 3);
  } else {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C_META_TEXT);
    doc.text('Facultatif', x, curY + 3);
  }
  curY += INPUT_LABEL_H;

  doc.setFillColor(...C_INPUT_BG);
  doc.setDrawColor(...C_INPUT_BORDER);
  doc.setLineWidth(0.4);
  doc.roundedRect(x, curY, w, INPUT_BOX_H, 1.5, 1.5, 'FD');

  if (placeholder) {
    const truncated = placeholder.length > 80 ? placeholder.slice(0, 77) + '...' : placeholder;
    doc.setFontSize(INPUT_FS);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...C_META_TEXT);
    doc.text(truncated, x + 3, curY + INPUT_BOX_H / 2 + INPUT_FS * 0.352778 / 2 - 0.3, { maxWidth: w - 6 });
  }

  // Dotted writing guideline inside the box
  doc.setDrawColor(...C_INPUT_BORDER);
  doc.setLineWidth(0.25);
  doc.setLineDashPattern([0.8, 1.2], 0);
  const lineY = curY + INPUT_BOX_H - 2.5;
  doc.line(x + 3, lineY, x + w - 3, lineY);
  doc.setLineDashPattern([], 0);
}

// --- Page setup helper ---
function setupPage(
  doc: jsPDF,
  logo: { data: string; aspect: number } | null,
  missionName: string,
  generatedAt: string,
  pageNum: number,
  totalPages: number,
  x: number, w: number, pageH: number,
): number {
  drawHeader(doc, x, MARGIN, w, missionName, logo);
  drawMetaLine(doc, x, MARGIN + HEADER_H, w, generatedAt, pageNum, totalPages);
  drawFooter(doc, x, pageH, w, missionName);
  return MARGIN + HEADER_H + META_H + 4;
}

// --- Main export ---

export async function generateMissionDetailsPdf(options: GenerateMissionDetailsPdfOptions): Promise<void> {
  const { name, description, estimated_duration_minutes, service, items, recurrence } = options;

  const logo = await loadLogo('/logo-tasq-fh-blanc.png');
  const generatedAt = new Date().toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  const doc      = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const PAGE_W   = doc.internal.pageSize.getWidth();
  const PAGE_H   = doc.internal.pageSize.getHeight();
  const USABLE_W = PAGE_W - 2 * MARGIN;
  const CONTENT_BOTTOM = PAGE_H - MARGIN - FOOTER_H - 4;

  // Text column starts after the number + checkbox columns
  const TEXT_X    = MARGIN + ROW_NUM_W + ROW_CHECK_W;
  const TEXT_MAX_W = USABLE_W - ROW_NUM_W - ROW_CHECK_W - 2;

  const totalPages = estimatePageCount(options, doc, USABLE_W, TEXT_MAX_W, PAGE_H, CONTENT_BOTTOM);

  let pageNum = 1;
  let y = setupPage(doc, logo, name, generatedAt, pageNum, totalPages, MARGIN, USABLE_W, PAGE_H);

  function ensureSpace(needed: number): void {
    if (y + needed > CONTENT_BOTTOM) {
      doc.addPage('a4', 'portrait');
      pageNum++;
      y = setupPage(doc, logo, name, generatedAt, pageNum, totalPages, MARGIN, USABLE_W, PAGE_H);
    }
  }

  // --- Mission name ---
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  const wrappedName = doc.splitTextToSize(name, USABLE_W - 4) as string[];
  const nameBlockH  = wrappedName.length * 7 + 4;
  ensureSpace(nameBlockH);
  doc.setTextColor(...C_TITLE_TEXT);
  doc.text(wrappedName, MARGIN, y + 6);
  y += nameBlockH;

  // --- Description ---
  if (description && description.trim().length > 0) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    const descLines = doc.splitTextToSize(description.trim(), USABLE_W - 12) as string[];
    const descH = descLines.length * 4.5 + 8;

    ensureSpace(descH + 3);
    doc.setFillColor(...C_SECTION_BG);
    doc.setDrawColor(...C_BORDER);
    doc.setLineWidth(0.25);
    doc.roundedRect(MARGIN, y, USABLE_W, descH, 2, 2, 'FD');
    doc.setTextColor(...C_BODY_TEXT);
    doc.text(descLines, MARGIN + 5, y + 5.5);
    y += descH + 4;
  }

  // --- Metadata row: duration + service ---
  const hasMeta = (estimated_duration_minutes && estimated_duration_minutes > 0) || service;
  if (hasMeta) {
    ensureSpace(12);
    let badgeX = MARGIN;

    if (estimated_duration_minutes && estimated_duration_minutes > 0) {
      const label = `Durée : ${formatDuration(estimated_duration_minutes)}`;
      const bw = drawBadge(doc, label, badgeX, y, C_BADGE_BG, C_BORDER, C_BADGE_TEXT, 7.5);
      badgeX += bw + 4;
    }

    if (service) {
      const palette = getServicePalette(service.color);
      drawBadge(doc, service.name, badgeX, y, palette.bg, palette.border, palette.text, 7.5);
    }

    y += 10;
  }

  // --- Recurrence block ---
  if (recurrence) {
    const recurrenceText = formatRecurrenceText(recurrence);
    ensureSpace(13);
    const recH = 10;
    doc.setFillColor(...C_RECUR_BG);
    doc.setDrawColor(...C_RECUR_BORDER);
    doc.setLineWidth(0.25);
    doc.roundedRect(MARGIN, y, USABLE_W, recH, 2, 2, 'FD');

    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...C_RECUR_TEXT);
    const labelW = doc.getTextWidth('Récurrence : ');
    doc.text('Récurrence : ', MARGIN + 5, y + recH / 2 + 1.5);
    doc.setFont('helvetica', 'normal');
    doc.text(recurrenceText, MARGIN + 5 + labelW, y + recH / 2 + 1.5);
    y += recH + 3; // reduced from 5
  }

  // --- Section separator + tasks header ---
  ensureSpace(16);
  doc.setDrawColor(...C_BORDER);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, y, MARGIN + USABLE_W, y);
  y += 5;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C_TITLE_TEXT);
  doc.text(`Liste des tâches (${items.length})`, MARGIN, y + 3.5);
  y += 10;

  // --- Task items ---
  if (items.length === 0) {
    ensureSpace(10);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...C_META_TEXT);
    doc.text('Aucune tâche définie pour cette mission.', MARGIN, y + 4);
    y += 10;
  } else {
    for (let i = 0; i < items.length; i++) {
      const item     = items[i];
      const metrics  = measureTaskRow(doc, item, TEXT_MAX_W);
      const { titleLines, rowH } = metrics;

      const hasTextInput = !!item.requires_text_input;
      const isRequired   = !!item.is_text_input_required;

      ensureSpace(rowH + ROW_GAP);

      const rowY = y;

      // Row number — right-aligned inside its column, vertically centred on first text line
      const firstLineBaselineY = rowY + ROW_PAD_TOP + TITLE_LS * 0.82;
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...C_META_TEXT);
      doc.text(`${i + 1}.`, MARGIN + ROW_NUM_W - 1, firstLineBaselineY, { align: 'right' });

      // Checkbox — vertically centred on first text line
      const checkY = firstLineBaselineY - ROW_CHECK_SZ * 0.72;
      drawCheckbox(doc, MARGIN + ROW_NUM_W, checkY);

      // Task title — all wrapped lines
      doc.setFontSize(TITLE_FS);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...C_BODY_TEXT);
      for (let li = 0; li < titleLines.length; li++) {
        doc.text(titleLines[li], TEXT_X, rowY + ROW_PAD_TOP + (li + 1) * TITLE_LS - 0.5);
      }

      let afterTitleY = rowY + ROW_PAD_TOP + titleLines.length * TITLE_LS;

      // Text input box — drawn below the title
      if (hasTextInput) {
        drawTextInputBox(
          doc,
          TEXT_X,
          afterTitleY,
          TEXT_MAX_W,
          item.text_input_placeholder,
          isRequired,
        );
      }

      // Subtle row separator line
      doc.setDrawColor(...C_ROW_SEP);
      doc.setLineWidth(0.15);
      doc.line(MARGIN, rowY + rowH, MARGIN + USABLE_W, rowY + rowH);

      y += rowH + ROW_GAP;
    }
  }

  const slug = normalizeSlug(name) || 'mission';
  doc.save(`fiche-mission-${slug}.pdf`);
}

// --- Page count estimator ---
// Uses the same measureTaskRow() function as the render pass to guarantee matching heights.
function estimatePageCount(
  options: GenerateMissionDetailsPdfOptions,
  doc: jsPDF,
  usableW: number,
  textMaxW: number,
  pageH: number,
  contentBottom: number,
): number {
  const { name, description, estimated_duration_minutes, service, items, recurrence } = options;
  const startY = MARGIN + HEADER_H + META_H + 4;
  let y = startY;
  let pages = 1;

  function check(needed: number) {
    if (y + needed > contentBottom) {
      pages++;
      y = startY;
    }
  }

  // Name
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  const nameLines = doc.splitTextToSize(name, usableW - 4) as string[];
  const nameH = nameLines.length * 7 + 4;
  check(nameH);
  y += nameH;

  // Description
  if (description && description.trim().length > 0) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    const descLines = doc.splitTextToSize(description.trim(), usableW - 12) as string[];
    const descH = descLines.length * 4.5 + 8;
    check(descH);
    y += descH + 4;
  }

  // Meta badges
  if ((estimated_duration_minutes && estimated_duration_minutes > 0) || service) {
    check(12);
    y += 10;
  }

  // Recurrence (gap reduced from 5 to 3 → total 13 instead of 15)
  if (recurrence) {
    check(13);
    y += 13;
  }

  // Tasks section header
  check(15);
  y += 15;

  // Task rows — uses the same measurement as the render pass
  if (items.length === 0) {
    check(10);
    y += 10;
  } else {
    for (const item of items) {
      const { rowH } = measureTaskRow(doc, item, textMaxW);
      check(rowH + ROW_GAP);
      y += rowH + ROW_GAP;
    }
  }

  return pages;
}
